import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import DayPicker from 'modules/core/components/Inputs/DayPicker'
import Text from 'modules/core/components/Inputs/Text'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useNavigation } from 'modules/core/hooks/useNavigation'

import DashboardEditVariablesModal from 'modules/reports/components/DashboardEditVariablesModal'
import TeamPicker from 'modules/reports/components/TeamPicker'
import type Dashboard from 'modules/reports/store/models/Dashboard'
import { VARIABLE_TYPES } from 'modules/reports/store/models/Dashboard'
import type { Variable } from 'modules/reports/store/models/Report'

import FormComponent, { VariableForm } from './VariableForm'
import OptionsButton from './_OptionsButton'
import { VariableValueForm } from './variableValueForm'

const FilterWrapper = ({
  form,
  children,
  dashboard,
  canEdit,
}: {
  form: VariableValueForm
  children: React.ReactNode
  dashboard: Dashboard
  canEdit: boolean
}) => {
  return (
    <Box inline flexDirection="row" alignItems="flexEnd">
      {children}

      {canEdit && (
        <Box inline padding="s6">
          <OptionsButton form={form} dashboard={dashboard} />
        </Box>
      )}
    </Box>
  )
}

const Filters = observer(
  ({
    filterForms,
    dashboard,
    canEdit,
  }: Omit<Props, 'showAddFilterModal' | 'setShowAddFilterModal'>) => {
    return (
      <>
        {filterForms.map((form) => {
          const key = form.get('name').value

          switch (form.get('type').value) {
            case VARIABLE_TYPES.date:
              return (
                <FilterWrapper form={form} dashboard={dashboard} canEdit={canEdit} key={key}>
                  <DayPicker
                    label={form.get('name').value}
                    field={form.get('value')}
                    valueFormat="backend"
                  />
                </FilterWrapper>
              )

            case VARIABLE_TYPES.team:
              return (
                <FilterWrapper form={form} dashboard={dashboard} canEdit={canEdit} key={key}>
                  <TeamPicker label={form.get('name').value} field={form.get('value')} />
                </FilterWrapper>
              )

            case VARIABLE_TYPES.text:
            default:
              return (
                <FilterWrapper form={form} dashboard={dashboard} canEdit={canEdit} key={key}>
                  <Text label={form.get('name').value} field={form.get('value')} />
                </FilterWrapper>
              )
          }
        })}
      </>
    )
  }
)

interface Props {
  filterForms: VariableValueForm[]
  dashboard: Dashboard
  canEdit: boolean
  showAddFilterModal: boolean
  setShowAddFilterModal: (value: boolean) => () => void
}

const DashboardFilters = ({
  filterForms,
  dashboard,
  canEdit,
  showAddFilterModal,
  setShowAddFilterModal,
}: Props) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const variables = dashboard.variables

  const form = React.useRef(
    new VariableForm({
      type: VARIABLE_TYPES.text,
    })
  ).current

  React.useEffect(() => {
    form.resetValues()
  }, [form, showAddFilterModal])

  const navigateToDashboard = () => {
    navigate({
      pathname: navigation.reports.dashboards.dashboardId.list.fullPath({
        dashboard_id: dashboard.id,
      }),
    })
  }

  const { running: isProcessing, performAction: onSubmit } = useAsyncAction(
    async (closePortal: ClosePortalFn) => {
      variables.push(form.data() as Variable)
      await dashboard.update(
        {
          variables,
        },
        {}
      )
      closePortal()
      navigateToDashboard()
    },
    {
      successFlash: true,
      errorFlash: false,
    }
  )

  return (
    <>
      {filterForms.length > 0 && (
        <Box
          flexDirection="row"
          flexWrap="wrap"
          alignItems="flexEnd"
          justifyContent="flexStart"
          gap="s24"
        >
          <Filters filterForms={filterForms} dashboard={dashboard} canEdit={canEdit} />
        </Box>
      )}

      {showAddFilterModal && (
        <DashboardEditVariablesModal
          onClose={() => {
            setShowAddFilterModal(false)
            navigateToDashboard()
          }}
          submitDisabled={isProcessing || !form.isCompleted}
          onSubmit={onSubmit}
        >
          <FormComponent form={form} readOnly={false} variables={variables} />
        </DashboardEditVariablesModal>
      )}
    </>
  )
}

export default observer(DashboardFilters)
